import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Camera, Clock, Clapperboard } from 'lucide-react';
import MainLayout from '../../components/MainLayout/MainLayout';
import Carousel from '../../components/Common/Carousel';
import CarouselItem from '../../components/Common/CarouselItem';
import ContentCard from '../../components/Common/ContentCard';
import SecureImage from '../../components/Media/SecureImage';
import type { ContentItem } from '../../types';
import useSEO from '../../hooks/useSEO';
import { useMediaDuration, formatDuration as formatMediaDuration } from '../../hooks/useMediaDuration';
import { config } from '../../config';

// Backend API response type based on the curl response
interface ContentType {
  id: string;
  title: string;
  description: string;
  slug?: string;
  contentType: string;
  status: string;
  category: string;
  thumbnailUrl?: string;
  mediaUrl?: string;
  previewUrl?: string;
  trailerUrl?: string;
  contentBody?: string;
  durationSeconds: number;
  fileSizeMb: number;
  viewCount: number;
  likeCount: number;
  isFeatured: boolean;
  isPremium: boolean;
  isLive: boolean;
  liveStatus?: string;
  scheduledStartTime?: string;
  actualStartTime?: string;
  currentViewers: number;
  maxViewers: number;
  author: {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    isVerified: boolean;
    isActive: boolean;
  };
  creatorName: string;
  channelName?: string;
  externalId?: string;
  imdbId?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  tags?: string[];
  cinema: boolean;
  content: boolean;
  podcast: boolean;
  animation: boolean;
  sneakPeek: boolean;
  liveVideo: boolean;
  mediaContent: boolean;
  originalsContent: boolean;
  currentlyLive: boolean;
}

interface SneakPeeksResponse {
  message: string;
  success: boolean;
  data: {
    content: ContentType[];
    pageable: {
      pageNumber: number;
      pageSize: number;
      sort: {
        empty: boolean;
        unsorted: boolean;
        sorted: boolean;
      };
      offset: number;
      paged: boolean;
      unpaged: boolean;
    };
    totalPages: number;
    totalElements: number;
    last: boolean;
    size: number;
    number: number;
    sort: {
      empty: boolean;
      unsorted: boolean;
      sorted: boolean;
    };
    numberOfElements: number;
    first: boolean;
    empty: boolean;
  };
  timestamp: string;
}

const SneakPeeksPage = () => {
  // Apply SEO for sneak peeks page
  useSEO();
  
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [sneakPeeks, setSneakPeeks] = useState<ContentType[]>([]);
  const [stats, setStats] = useState({ total: 0, totalViews: 0, avgDuration: 0 });

  // Generate enhanced tags for sneak peeks
  const enhanceTags = (content: ContentType): string[] => {
    const tags: string[] = [];
    
    // Add existing tags if available (backend doesn't provide tags, so this will be skipped)
    if (content.tags && Array.isArray(content.tags) && content.tags.length > 0) {
      tags.push(...content.tags);
    }
    
    // Add category as a tag if available
    if (content.category) {
      tags.push(content.category);
    }
    
    // Add sneak peek-specific tags
    tags.push('Sneak Peek', 'Preview');
    
    // Add duration-based tags
    const durationMinutes = Math.floor((content.durationSeconds || 0) / 60);
    if (durationMinutes < 2) {
      tags.push('Quick Look');
    } else if (durationMinutes > 5) {
      tags.push('Extended Preview');
    }
    
    // Add featured tag if it's featured
    if (content.isFeatured) {
      tags.push('Featured');
    }
    
    // Add premium tag if it's premium
    if (content.isPremium) {
      tags.push('Premium');
    }
    
    // Add view-based tags
    if (content.viewCount && content.viewCount > 3000) {
      tags.push('Popular');
    }
    
    // Add exclusive tag
    tags.push('Exclusive');
    
    return [...new Set(tags)].slice(0, 4); // Remove duplicates and limit to 4 tags
  };

  // Convert ContentType to ContentItem
  const convertToContentItem = useCallback((content: ContentType): ContentItem => ({
    id: content.id,
    title: content.title,
    description: content.description || 'No description available',
    thumbnail: content.thumbnailUrl || `https://images.unsplash.com/photo-1485846234645-a62644f84728?w=400&h=600&fit=crop&auto=format&q=80`,
    duration: Math.floor((content.durationSeconds || 0) / 60), // Convert seconds to minutes
    type: 'sneak-peek',
    tags: enhanceTags(content),
    views: content.viewCount || 0,
    likes: content.likeCount || 0,
    createdAt: content.createdAt,
    category: content.category
  }), []);

  // Smart grouping with Featured, Trending, and See More sections
  const getGroupedSneakPeeks = useCallback(() => {
    if (sneakPeeks.length === 0) {
      return { featured: [], trending: [], seeMore: [] };
    }

    // Convert to ContentItems first
    const convertedItems = sneakPeeks.map(convertToContentItem);
    
    // Sort by newest first (default behavior)
    const sortedItems = [...convertedItems].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    // Featured: items with featured flag or high view count
    const potentialFeatured = sortedItems.filter(item => {
      const originalItem = sneakPeeks.find(s => s.id === item.id);
      return originalItem?.isFeatured || (item.views && item.views > 3000);
    }).slice(0, 12);
    
    // Trending: items with high recent views (excluding featured)
    const featuredIds = new Set(potentialFeatured.map(item => item.id));
    const potentialTrending = sortedItems
      .filter(item => !featuredIds.has(item.id))
      .filter(item => item.views && item.views > 1500)
      .slice(0, 12);
    
    // Apply minimum threshold (5 items) - if not enough, move to See More
    const minThreshold = 5;
    const featured = potentialFeatured.length >= minThreshold ? potentialFeatured : [];
    const trending = potentialTrending.length >= minThreshold ? potentialTrending : [];
    
    // See More: remaining items + items that didn't meet threshold (avoiding duplicates)
    const usedIds = new Set([...featured.map(item => item.id), ...trending.map(item => item.id)]);
    const remainingItems = sortedItems.filter(item => !usedIds.has(item.id));
    
    // Items that didn't meet threshold
    const thresholdItems = [
      ...(potentialFeatured.length < minThreshold ? potentialFeatured : []),
      ...(potentialTrending.length < minThreshold ? potentialTrending : [])
    ];
    
    // Use Map to deduplicate by ID
    const seeMoreSet = new Map();
    [...thresholdItems, ...remainingItems].forEach(item => {
      seeMoreSet.set(item.id, item);
    });
    const seeMore = Array.from(seeMoreSet.values());

    return { featured, trending, seeMore };
  }, [sneakPeeks, convertToContentItem]);

  // Load sneak peeks data
  useEffect(() => {
    loadSneakPeeksData();
  }, []);

  const loadSneakPeeksData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the content API endpoint we discovered
      const response = await fetch(`${config.apiBaseUrl}/content/sneak-peeks`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: SneakPeeksResponse = await response.json();
      
      setSneakPeeks(data.data.content);
      
      // Calculate average duration
      const avgDuration = data.data.content.length > 0 ? 
        data.data.content.reduce((sum, item) => sum + (item.durationSeconds || 0), 0) / data.data.content.length / 60 : 0;
      
      setStats({
        total: data.data.totalElements,
        totalViews: data.data.content.reduce((sum, item) => sum + (item.viewCount || 0), 0),
        avgDuration: avgDuration
      });
      
    } catch (err) {
      console.error('Error loading sneak peeks:', err);
      setError('Failed to load sneak peeks. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const featuredSneakPeek = sneakPeeks.find(peek => peek.isFeatured) || sneakPeeks[0];
  const heroDuration = useMediaDuration(featuredSneakPeek?.mediaUrl, featuredSneakPeek?.durationSeconds);

  // Format view count for display
  const formatViewCount = (views: number): string => {
    if (views >= 1000000) {
      const millions = views / 1000000;
      return millions % 1 === 0 ? `${millions.toFixed(0)}M` : `${millions.toFixed(1)}M`;
    } else if (views >= 1000) {
      const thousands = views / 1000;
      return thousands % 1 === 0 ? `${thousands.toFixed(0)}K` : `${thousands.toFixed(1)}K`;
    }
    return views.toString();
  };

  const formatDuration = (duration?: number) => {
    if (!duration) return '0:00';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading sneak peeks...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 mb-4">
              <Camera className="h-12 w-12 mx-auto mb-2" />
              <p className="text-lg font-semibold">Error Loading Sneak Peeks</p>
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-slate-900 via-neutral-900 to-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <Camera className="h-8 w-8 text-orange-400 mr-3" />
                  <span className="text-orange-400 font-semibold">Sneak Peeks</span>
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                    Behind the Scenes
                  </span>
                </h1>
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Get exclusive behind-the-scenes access and sneak peeks at upcoming content. 
                  See how your favorite shows and movies come to life before anyone else.
                </p>
                
                {/* Stats */}
                <div className="flex flex-wrap gap-8 mb-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-orange-400 bg-clip-text text-transparent">
                      {stats.total}+
                    </div>
                    <div className="text-sm text-gray-400">Sneak Peeks</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
                      {stats.totalViews >= 1000 ? `${formatViewCount(stats.totalViews)}+` : `${stats.totalViews}+`}
                    </div>
                    <div className="text-sm text-gray-400">Total Views</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold bg-gradient-to-r from-orange-400 to-cyan-400 bg-clip-text text-transparent">
                      {stats.avgDuration.toFixed(0)}m
                    </div>
                    <div className="text-sm text-gray-400">Avg. Duration</div>
                  </div>
                </div>

                {/* Animation Elements */}
                <div className="flex items-center space-x-4 mb-8">
                  <Camera className="w-6 h-6 text-orange-400 animate-pulse" />
                  <div className="flex space-x-2">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="w-3 h-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 0.2}s` }}
                      />
                    ))}
                  </div>
                  <Clapperboard className="w-6 h-6 text-orange-400 animate-pulse" />
                </div>
              </div>
              
              {/* Featured Sneak Peek */}
              {featuredSneakPeek && (
                <div className="relative">
                  <div 
                    className="relative z-10 rounded-3xl overflow-hidden shadow-2xl cursor-pointer transform transition-transform hover:scale-105"
                    onClick={() => navigate(`/sneak-peeks/${featuredSneakPeek.id}`)}
                  >
                    <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 to-slate-900 relative">
                      <SecureImage 
                        src={featuredSneakPeek.thumbnailUrl?.trim() ? 
                             featuredSneakPeek.thumbnailUrl :
                             `https://images.unsplash.com/photo-1485846234645-a62644f84728?w=400&h=600&fit=crop&auto=format&q=80`
                        } 
                        alt={featuredSneakPeek.title}
                        className="w-full h-full object-cover"
                      />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20"></div>
                      
                      <div className="absolute top-4 left-4 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        Sneak Peek
                      </div>
                      
                      <div className="absolute bottom-6 left-6 right-6 text-white">
                        {featuredSneakPeek.category && (
                          <div className="bg-orange-500 text-white px-2 py-1 rounded text-xs font-medium mb-2 inline-block">
                            {featuredSneakPeek.category}
                          </div>
                        )}
                        <h3 className="text-2xl font-bold mb-2">{featuredSneakPeek.title}</h3>
                        <p className="text-gray-200 text-sm mb-4 line-clamp-2">{featuredSneakPeek.description}</p>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {heroDuration ? formatMediaDuration(heroDuration) : '0:00'}
                          </div>
                          <div className="flex items-center">
                            <Eye className="w-4 h-4 mr-1" />
                            {formatViewCount(featuredSneakPeek.viewCount)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-8">
          <div className="text-center mb-8 px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Exclusive Sneak Peeks</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get behind-the-scenes access and exclusive previews of upcoming content. 
              See how your favorite productions come together before they're released.
            </p>
          </div>

          {/* Carousels by Category */}
          {Object.entries(getGroupedSneakPeeks()).map(([category, items]) => {
            if (items.length === 0) return null;
            
            const categoryLabels: {[key: string]: string} = {
              featured: 'Featured',
              trending: 'Trending',
              seemore: 'See More'
            };

            return (
              <div key={category} className="mb-6">
                <Carousel title={categoryLabels[category.toLowerCase()] || category.charAt(0).toUpperCase() + category.slice(1)} id={`sneakpeeks-${category}`}>
                  {items.map((item) => (
                    <CarouselItem key={item.id}>
                      <ContentCard {...item} />
                    </CarouselItem>
                  ))}
                </Carousel>
              </div>
            );
          })}

          {/* Empty State */}
          {sneakPeeks.length === 0 && !loading && (
            <div className="max-w-7xl mx-auto text-center py-12">
              <Camera className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No sneak peeks available</h3>
              <p className="text-gray-600">
                Check back later for exclusive behind-the-scenes content.
              </p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default SneakPeeksPage;